// src/features/chales/ChalesTab.jsx
import React, { useState, useMemo } from "react";
import { useChalesData } from "./hooks/useChalesData";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import ChaletCard from "./components/ChaletCard";
import AddReservationModal from "./components/AddReservationModal";
import EditReservationModal from "./components/EditReservationModal";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );

function ChalesTab({ userRole, searchTerm }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);

  const {
    chales,
    isLoading,
    error,
    addPayment,
    removePayment,
    totals,
    launchFinancials,
    isLaunching,
    addReservation,
    deleteReservation,
    updateReservation,
    addCredit,
  } = useChalesData(selectedDate, searchTerm);

  const handleDateChange = (event) => {
    setSelectedDate(new Date(event.target.value + "T00:00:00"));
  };

  const handleOpenEditModal = (chaletData) => {
    setEditingReservation(chaletData);
  };

  const availableChales = useMemo(() => {
    return chales.filter((c) => c.isAvailable).map((c) => c.chaletNumber);
  }, [chales]);

  const canEdit = userRole === "admin" || userRole === "editor";
  const isSearchMode = searchTerm && searchTerm.length > 3;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div className="w-auto">
          {isSearchMode ? (
            <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200">
              <strong>Modo de Busca:</strong> Exibindo resultados para '
              {searchTerm}'
            </div>
          ) : (
            <>
              <Label htmlFor="chale-date" className="font-semibold">
                Selecione a Data
              </Label>
              <Input
                type="date"
                id="chale-date"
                value={formatDateToYYYYMMDD(selectedDate)}
                onChange={handleDateChange}
                className="mt-2 force-light-datepicker"
              />
            </>
          )}
        </div>

        {canEdit && !isSearchMode && (
          <div className="flex gap-2">
            <Button onClick={() => setIsAddModalOpen(true)}>
              + Nova Reserva
            </Button>
          </div>
        )}
      </div>

      {isLoading && <LoadingSpinner />}
      {error && (
        <p className="text-red-500 text-center p-4 bg-red-50 rounded-md">
          Erro: {error}
        </p>
      )}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chales.map((chale) => (
              <ChaletCard
                key={chale.chaletNumber}
                chale={chale}
                onAddPayment={addPayment}
                onRemovePayment={removePayment}
                onDeleteReservation={deleteReservation}
                onEditReservation={handleOpenEditModal}
                onAddCredit={addCredit}
                canEdit={canEdit}
              />
            ))}
          </div>

          {!isSearchMode && (
            <div className="mt-8 p-6 bg-white dark:bg-gray-800 border border-gray-200 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Totalização do Dia
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500 font-medium">Depósito</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(totals.depósito)}
                  </p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500 font-medium">PIX</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(totals.pix)}
                  </p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500 font-medium">Cartão</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(totals.cartão)}
                  </p>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500 font-medium">Dinheiro</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(totals.dinheiro)}
                  </p>
                </div>
                <div className="p-3 bg-gray-800 text-white rounded-lg border border-gray-800 dark:border-gray-200  col-span-2 md:col-span-1 flex flex-col justify-center">
                  <p className="text-sm font-medium">TOTAL GERAL</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(
                      totals.depósito +
                        totals.pix +
                        totals.cartão +
                        totals.dinheiro,
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                {canEdit && (
                  <button
                    onClick={launchFinancials}
                    disabled={isLaunching}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLaunching ? "Lançando..." : "Lançar no Histórico"}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <AddReservationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddReservation={addReservation}
        availableChales={availableChales}
        defaultCheckInDate={selectedDate}
      />

      {editingReservation && (
        <EditReservationModal
          isOpen={!!editingReservation}
          onClose={() => setEditingReservation(null)}
          onUpdateReservation={updateReservation}
          reservationData={editingReservation}
        />
      )}
    </div>
  );
}

export default ChalesTab;
